export const BASE_PPI = 72
const SAFETY_MARGIN_OBJECT_NAME = 'safety-margin'
const TRANSFORM_EPSILON_PX = 0.5
const TRANSFORM_PROPERTIES = [
  'left',
  'top',
  'scaleX',
  'scaleY',
  'angle',
  'skewX',
  'skewY',
  'flipX',
  'flipY',
]
const TRANSFORM_PROGRESS_EVENTS = [
  'object:moving',
  'object:rotating',
  'object:scaling',
  'object:skewing',
  'object:resizing',
]

function isArtworkObject(object) {
  return object &&
    object.name !== SAFETY_MARGIN_OBJECT_NAME &&
    object.type !== 'activeSelection' &&
    object.uploadedImageId != null
}

function objectCorners(object) {
  object.setCoords?.()
  const coords = object.getCoords?.()
  if (coords?.length >= 3) return coords.map(point => ({ x: point.x, y: point.y }))

  const bounds = object.getBoundingRect?.() || {
    left: Number(object.left) || 0,
    top: Number(object.top) || 0,
    width: (Number(object.width) || 0) * (Number(object.scaleX) || 1),
    height: (Number(object.height) || 0) * (Number(object.scaleY) || 1),
  }
  return [
    { x: bounds.left, y: bounds.top },
    { x: bounds.left + bounds.width, y: bounds.top },
    { x: bounds.left + bounds.width, y: bounds.top + bounds.height },
    { x: bounds.left, y: bounds.top + bounds.height },
  ]
}

function projection(polygon, axis) {
  const values = polygon.map(point => point.x * axis.x + point.y * axis.y)
  return { min: Math.min(...values), max: Math.max(...values) }
}

function polygonAxes(polygon) {
  return polygon.map((point, index) => {
    const next = polygon[(index + 1) % polygon.length]
    const x = -(next.y - point.y)
    const y = next.x - point.x
    const length = Math.hypot(x, y)
    return length > 0 ? { x: x / length, y: y / length } : null
  }).filter(Boolean)
}

export function polygonsOverlap(first, second, epsilon = 0.000001) {
  if (first.length < 3 || second.length < 3) return false

  return [...polygonAxes(first), ...polygonAxes(second)].every(axis => {
    const firstProjection = projection(first, axis)
    const secondProjection = projection(second, axis)
    return !(
      firstProjection.max <= secondProjection.min + epsilon ||
      secondProjection.max <= firstProjection.min + epsilon
    )
  })
}

function layoutItemCorners(item) {
  const x = Number(item?.x_in)
  const y = Number(item?.y_in)
  const width = Number(item?.width_in)
  const height = Number(item?.height_in)
  const rotation = Number(item?.rotation_deg || 0)
  if (![x, y, width, height, rotation].every(Number.isFinite) || width <= 0 || height <= 0) {
    return null
  }

  const radians = rotation * Math.PI / 180
  const cosine = Math.cos(radians)
  const sine = Math.sin(radians)

  return [
    [0, 0],
    [width, 0],
    [width, height],
    [0, height],
  ].map(([cornerX, cornerY]) => ({
    x: x + (cornerX * cosine) - (cornerY * sine),
    y: y + (cornerX * sine) + (cornerY * cosine),
  }))
}

export function firstOverlappingSheetNumber(layout) {
  const sheets = Array.isArray(layout?.sheets) ? layout.sheets : []

  for (let sheetIndex = 0; sheetIndex < sheets.length; sheetIndex += 1) {
    const polygons = (sheets[sheetIndex]?.items || []).map(layoutItemCorners).filter(Boolean)
    for (let firstIndex = 0; firstIndex < polygons.length; firstIndex += 1) {
      for (let secondIndex = firstIndex + 1; secondIndex < polygons.length; secondIndex += 1) {
        if (polygonsOverlap(polygons[firstIndex], polygons[secondIndex])) return sheetIndex + 1
      }
    }
  }

  return null
}

function artworkMembers(target) {
  if (isArtworkObject(target)) return [target]
  return (target?.getObjects?.() || []).filter(isArtworkObject)
}

export function validateArtworkTransform(canvas, target, sheetSize) {
  const members = artworkMembers(target)
  if (!members.length) return null

  target.setCoords?.()
  const polygons = members.map(objectCorners)
  const sheetWidthPx = Number(sheetSize.width_in) * BASE_PPI
  const sheetHeightPx = Number(sheetSize.height_in) * BASE_PPI
  const outsideSheet = polygons.flat().some(point => (
    point.x < -TRANSFORM_EPSILON_PX ||
    point.y < -TRANSFORM_EPSILON_PX ||
    point.x > sheetWidthPx + TRANSFORM_EPSILON_PX ||
    point.y > sheetHeightPx + TRANSFORM_EPSILON_PX
  ))
  if (outsideSheet) return 'Artwork must stay fully inside the sheet.'

  const others = (canvas.getObjects?.() || []).filter(object => (
    !members.includes(object) && isArtworkObject(object)
  )).map(objectCorners)
  const overlapsArtwork = polygons.some((polygon, index) => (
    [...others, ...polygons.slice(index + 1)].some(other => polygonsOverlap(polygon, other))
  ))
  if (overlapsArtwork) return 'Artwork can’t overlap another image. Move it to an open area.'

  return null
}

function captureTransform(object) {
  return Object.fromEntries(TRANSFORM_PROPERTIES.map(property => [property, object[property]]))
}

function restoreTransform(object, transform) {
  if (!transform) return
  if (typeof object.set === 'function') object.set(transform)
  else Object.assign(object, transform)
  object.setCoords?.()
  artworkMembers(object).forEach(member => member.setCoords?.())
}

// Capture each edit's starting geometry, not the last object:modified event:
// undo/redo and auto-arrange can replace geometry without firing that event.
export function installTransformGuard(canvas, getSheetSize, onValidationChange) {
  const pendingTransforms = new WeakMap()
  canvas.on('before:transform', ({ transform }) => {
    const target = transform?.target
    if (!artworkMembers(target).length) return

    const initial = captureTransform(target)
    pendingTransforms.set(target, {
      initial,
      lastValid: initial,
      reportedMessage: null,
    })
  })

  const trackTransform = ({ target }) => {
    const pending = target && pendingTransforms.get(target)
    if (!pending) return

    const message = validateArtworkTransform(canvas, target, getSheetSize())
    if (!message) pending.lastValid = captureTransform(target)
    if (message !== pending.reportedMessage) {
      pending.reportedMessage = message
      onValidationChange?.(message)
    }
  }
  TRANSFORM_PROGRESS_EVENTS.forEach(event => canvas.on(event, trackTransform))

  canvas.on('object:modified', ({ target }) => {
    const pending = target && pendingTransforms.get(target)
    if (!pending) return
    pendingTransforms.delete(target)
    const message = validateArtworkTransform(canvas, target, getSheetSize())
    if (message) {
      restoreTransform(target, pending.lastValid || pending.initial)
      canvas.requestRenderAll?.()
    }
    onValidationChange?.(message)
  })
}
